import { DateEditor } from '@slickgrid-universal/common';
import { EditorArguments } from 'angular-slickgrid';

export class CustomDateEditor extends DateEditor {
  private static readonly DATE_ONLY_REGEX = /^(\d{4})[-/](\d{2})[-/](\d{2})(?:T.*)?$/;
  private static readonly DISPLAY_DATE_REGEX = /^(\d{2})\/(\d{2})\/(\d{4})$/;

  private readonly handleDocumentKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') {
      return;
    }

    if (!this.isDateEditorEventTarget(event.target)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    this.hide();

    if (event.shiftKey) {
      this.grid.navigatePrev?.();
      return;
    }

    this.grid.navigateNext?.();
  };

  constructor(args: EditorArguments) {
    super(args);
  }

  override async init(): Promise<void> {
    await super.init();
    document.addEventListener('keydown', this.handleDocumentKeydown, true);
  }

  override destroy(): void {
    document.removeEventListener('keydown', this.handleDocumentKeydown, true);
    super.destroy();
  }

  override serializeValue(): any {
    const editorValue = this.readEditorDateValue();
    const editorDateOnly = this.toDateOnlyString(editorValue);
    if (editorDateOnly) {
      return editorDateOnly;
    }

    const serializedValue = super.serializeValue();
    return this.toDateOnlyString(serializedValue) ?? serializedValue;
  }

  private isDateEditorEventTarget(target: EventTarget | null): target is HTMLElement {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    if (target === this.editorDomElement) {
      return true;
    }

    if (target.closest('[data-vc="calendar"]')) {
      return true;
    }

    return !!target.closest('.vanilla-picker');
  }

  private readEditorDateValue(): unknown {
    const input = (this as any)._inputElm ?? this.editorDomElement;
    return input instanceof HTMLInputElement ? input.value : null;
  }

  private toDateOnlyString(value: unknown): string | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const yyyy = String(value.getFullYear());
      const mm = String(value.getMonth() + 1).padStart(2, '0');
      const dd = String(value.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const match = CustomDateEditor.DATE_ONLY_REGEX.exec(value);
    if (match) {
      const [, yyyy, mm, dd] = match;
      return `${yyyy}-${mm}-${dd}`;
    }

    const displayMatch = CustomDateEditor.DISPLAY_DATE_REGEX.exec(value);
    if (displayMatch) {
      const [, dd, mm, yyyy] = displayMatch;
      return `${yyyy}-${mm}-${dd}`;
    }

    return null;
  }
}
